/**
 * API functions for event management
 */
import { supabase } from '../supabase';
import { normalizeSupabaseError } from './errors';
import type {
  Event,
  EventWithDetails,
  EventCustomField,
  EventSignupOption,
  EventInvitee,
} from '../../types/database';

/**
 * Events for the public list. Returns top-level events (no parent_event_id):
 * - published + visibility=public events, and
 * - draft events that are parent events (so multi-site "coming soon" events like World Cup appear).
 * Invite-only events never appear here (use direct slug / mobile / admin).
 * Child sites are shown on the parent event page with registration per child.
 */
export async function getPublishedEvents(): Promise<Event[]> {
  const { data: published, error: pubError } = await supabase
    .from('events')
    .select('*')
    .eq('status', 'published')
    .eq('visibility', 'public')
    .is('parent_event_id', null)
    .order('start_date', { ascending: true });
  if (pubError) throw normalizeSupabaseError(pubError);

  const { data: draftParents, error: draftError } = await supabase
    .from('events')
    .select('*')
    .eq('status', 'draft')
    .eq('is_parent_event', true)
    .is('parent_event_id', null)
    .order('start_date', { ascending: true });
  if (draftError) throw normalizeSupabaseError(draftError);

  const combined = [...(published || []), ...(draftParents || [])];
  combined.sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
  return combined;
}

export async function getArchivedEvents(): Promise<Event[]> {
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .eq('status', 'archived')
    .order('start_date', { ascending: false });

  if (error) throw normalizeSupabaseError(error);
  return data || [];
}

export async function getAllEvents(): Promise<Event[]> {
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw normalizeSupabaseError(error);
  return data || [];
}

export async function getEventBySlug(slug: string): Promise<EventWithDetails | null> {
  const { data, error } = await supabase
    .from('events')
    .select(`
      *,
      schedule_items:event_schedule_items(*),
      contacts:event_contacts(*),
      gallery_images:event_gallery_images(*),
      custom_fields:event_custom_fields(*),
      signup_options:event_signup_options(*)
    `)
    .eq('slug', slug)
    .maybeSingle();

  if (error) throw normalizeSupabaseError(error);
  if (!data) return null;

  // Load child events if this is a parent event
  if (data.is_parent_event) {
    const childEvents = await getChildEvents(data.id);
    return { ...data, child_events: childEvents };
  }

  // Load parent event if this is a child event
  if (data.parent_event_id) {
    const parentEvent = await getParentEvent(data.id);
    return { ...data, parent_event: parentEvent ?? undefined };
  }

  return data;
}

export async function getEventById(id: string): Promise<EventWithDetails | null> {
  const { data, error } = await supabase
    .from('events')
    .select(`
      *,
      schedule_items:event_schedule_items(*),
      contacts:event_contacts(*),
      gallery_images:event_gallery_images(*),
      custom_fields:event_custom_fields(*),
      signup_options:event_signup_options(*)
    `)
    .eq('id', id)
    .maybeSingle();

  if (error) throw normalizeSupabaseError(error);
  if (!data) return null;

  // Load child events if this is a parent event
  if (data.is_parent_event) {
    const childEvents = await getChildEvents(data.id);
    return { ...data, child_events: childEvents };
  }

  // Load parent event if this is a child event
  if (data.parent_event_id) {
    const parentEvent = await getParentEvent(data.id);
    return { ...data, parent_event: parentEvent ?? undefined };
  }

  return data;
}

export async function createEvent(event: Partial<Event>): Promise<Event> {
  const { data, error } = await supabase
    .from('events')
    .insert(event)
    .select()
    .single();

  if (error) throw normalizeSupabaseError(error);
  return data;
}

export async function updateEvent(id: string, updates: Partial<Event>): Promise<Event> {
  const { data, error } = await supabase
    .from('events')
    .update(updates)
    .eq('id', id)
    .select()
    .maybeSingle();

  if (error) throw normalizeSupabaseError(error);
  if (!data) throw new Error('Not found');
  return data;
}

export async function deleteEvent(id: string): Promise<void> {
  const { error } = await supabase
    .from('events')
    .delete()
    .eq('id', id);

  if (error) throw normalizeSupabaseError(error);
}

export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Generate a unique slug based on an existing slug
 * Appends -2, -3, etc. until unique
 */
async function generateUniqueSlug(baseSlug: string): Promise<string> {
  let slug = baseSlug;
  let counter = 2;

  while (true) {
    const { data } = await supabase
      .from('events')
      .select('id')
      .eq('slug', slug)
      .maybeSingle();

    if (!data) {
      return slug;
    }

    slug = `${baseSlug}-${counter}`;
    counter++;
  }
}

/**
 * Clone an event with all related data
 * Creates a new event with duplicated schedule, contacts, gallery, and custom fields
 * Does NOT copy applications
 */
export async function cloneEvent(eventId: string): Promise<Event> {
  // Load the complete event with all related data
  const event = await getEventById(eventId);
  if (!event) {
    throw new Error('Event not found');
  }

  // Generate unique slug
  const baseSlug = `${event.slug}-copy`;
  const uniqueSlug = await generateUniqueSlug(baseSlug);

  // Create the cloned event (without related data)
  const { schedule_items, contacts, gallery_images, custom_fields, signup_options, child_events, parent_event, ...eventData } = event;

  const clonedEventData: Partial<Event> = {
    ...eventData,
    slug: uniqueSlug,
    title: `${event.title} (Copy)`,
    status: 'draft' as const,
    id: undefined,
    created_at: undefined,
    updated_at: undefined,
  };

  const { data: newEvent, error: eventError } = await supabase
    .from('events')
    .insert(clonedEventData)
    .select()
    .single();

  if (eventError) throw eventError;

  // Clone related records in parallel
  const clonePromises = [];

  // Clone schedule items
  if (schedule_items && schedule_items.length > 0) {
    const scheduleData = schedule_items.map(({ id, event_id, created_at, updated_at, ...item }) => ({
      ...item,
      event_id: newEvent.id,
    }));
    clonePromises.push(
      supabase.from('event_schedule_items').insert(scheduleData)
    );
  }

  // Clone contacts
  if (contacts && contacts.length > 0) {
    const contactsData = contacts.map(({ id, event_id, created_at, updated_at, ...contact }) => ({
      ...contact,
      event_id: newEvent.id,
    }));
    clonePromises.push(
      supabase.from('event_contacts').insert(contactsData)
    );
  }

  // Clone gallery images
  if (gallery_images && gallery_images.length > 0) {
    const galleryData = gallery_images.map(({ id, event_id, created_at, updated_at, ...image }) => ({
      ...image,
      event_id: newEvent.id,
    }));
    clonePromises.push(
      supabase.from('event_gallery_images').insert(galleryData)
    );
  }

  // Clone custom fields
  if (custom_fields && custom_fields.length > 0) {
    const fieldsData = custom_fields.map(({ id, event_id, created_at, updated_at, ...field }) => ({
      ...field,
      event_id: newEvent.id,
    }));
    clonePromises.push(
      supabase.from('event_custom_fields').insert(fieldsData)
    );
  }

  // Clone signup options
  if (signup_options && signup_options.length > 0) {
    const optionsData = signup_options.map(({ id, event_id, created_at, updated_at, ...option }) => ({
      ...option,
      event_id: newEvent.id,
    }));
    clonePromises.push(
      supabase.from('event_signup_options').insert(optionsData)
    );
  }

  // Wait for all clones to complete
  await Promise.all(clonePromises);

  return newEvent;
}

/**
 * Get child events for a parent event (multi-site)
 */
export async function getChildEvents(parentEventId: string): Promise<Event[]> {
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .eq('parent_event_id', parentEventId)
    .order('display_order', { ascending: true });

  if (error) throw normalizeSupabaseError(error);
  return data || [];
}

/**
 * Get event with child events (for parent events)
 */
export async function getEventWithChildren(eventId: string): Promise<EventWithDetails | null> {
  const event = await getEventById(eventId);
  if (!event) return null;

  const childEvents = await getChildEvents(eventId);

  return {
    ...event,
    child_events: childEvents,
  };
}

/**
 * Get parent event for a child event
 */
export async function getParentEvent(childEventId: string): Promise<Event | null> {
  // First get the child event to find its parent_event_id
  const { data: childEvent, error: childError } = await supabase
    .from('events')
    .select('parent_event_id')
    .eq('id', childEventId)
    .maybeSingle();

  if (childError || !childEvent?.parent_event_id) return null;

  // Get the parent event
  const { data: parentEvent, error: parentError } = await supabase
    .from('events')
    .select('*')
    .eq('id', childEvent.parent_event_id)
    .maybeSingle();

  if (parentError || !parentEvent) return null;
  return parentEvent;
}

/**
 * Get all events that can be parent events
 * (events that don't already have a parent and are marked as parent events)
 */
export async function getEligibleParentEvents(): Promise<Partial<Event>[]> {
  const { data, error } = await supabase
    .from('events')
    .select('id, slug, title, start_date, location_city')
    .is('parent_event_id', null)
    .eq('is_parent_event', true)
    .order('start_date', { ascending: false });

  if (error) throw normalizeSupabaseError(error);
  return (data || []) as unknown as Partial<Event>[];
}

// ============================================
// Event Custom Fields
// ============================================

/**
 * Get custom fields for an event, sorted by sort_order
 */
export async function getEventCustomFields(eventId: string): Promise<EventCustomField[]> {
  const { data, error } = await supabase
    .from('event_custom_fields')
    .select('*')
    .eq('event_id', eventId)
    .order('sort_order', { ascending: true });

  if (error) throw normalizeSupabaseError(error);
  return data || [];
}

/**
 * Create a new custom field for an event
 */
export async function createEventCustomField(
  eventId: string,
  field: Omit<EventCustomField, 'id' | 'event_id' | 'created_at' | 'updated_at'>
): Promise<EventCustomField> {
  const { data, error } = await supabase
    .from('event_custom_fields')
    .insert({ ...field, event_id: eventId })
    .select()
    .single();

  if (error) throw normalizeSupabaseError(error);
  return data;
}

/**
 * Update a custom field
 */
export async function updateEventCustomField(
  fieldId: string,
  updates: Partial<EventCustomField>
): Promise<EventCustomField> {
  const { data, error } = await supabase
    .from('event_custom_fields')
    .update(updates)
    .eq('id', fieldId)
    .select()
    .maybeSingle();

  if (error) throw normalizeSupabaseError(error);
  if (!data) throw new Error('Not found');
  return data;
}

/**
 * Delete a custom field
 */
export async function deleteEventCustomField(fieldId: string): Promise<void> {
  const { error } = await supabase
    .from('event_custom_fields')
    .delete()
    .eq('id', fieldId);

  if (error) throw normalizeSupabaseError(error);
}

/**
 * Update sort orders for multiple custom fields
 */
export async function updateCustomFieldSortOrders(
  updates: Array<{ id: string; sort_order: number }>
): Promise<void> {
  const { error } = await supabase.rpc('update_custom_field_sort_orders', {
    updates: updates,
  });

  if (error) {
    // Fallback to sequential updates if RPC doesn't exist
    for (const update of updates) {
      await supabase
        .from('event_custom_fields')
        .update({ sort_order: update.sort_order })
        .eq('id', update.id);
    }
  }
}

// ============================================
// Event Signup Options
// ============================================

/**
 * Get signup options for an event, sorted by display_order
 */
export async function getEventSignupOptions(eventId: string): Promise<EventSignupOption[]> {
  const { data, error } = await supabase
    .from('event_signup_options')
    .select('*')
    .eq('event_id', eventId)
    .order('display_order', { ascending: true });

  if (error) throw normalizeSupabaseError(error);
  return data || [];
}

/**
 * Create a new signup option for an event
 */
export async function createSignupOption(
  eventId: string,
  option: Omit<EventSignupOption, 'id' | 'event_id' | 'created_at' | 'updated_at'>
): Promise<EventSignupOption> {
  const { data, error } = await supabase
    .from('event_signup_options')
    .insert({ ...option, event_id: eventId })
    .select()
    .single();

  if (error) throw normalizeSupabaseError(error);
  return data;
}

/**
 * Update a signup option
 */
export async function updateSignupOption(
  optionId: string,
  updates: Partial<Omit<EventSignupOption, 'id' | 'event_id' | 'created_at' | 'updated_at'>>
): Promise<EventSignupOption> {
  const { data, error } = await supabase
    .from('event_signup_options')
    .update(updates)
    .eq('id', optionId)
    .select()
    .maybeSingle();

  if (error) throw normalizeSupabaseError(error);
  if (!data) throw new Error('Not found');
  return data;
}

/**
 * Delete a signup option
 */
export async function deleteSignupOption(optionId: string): Promise<void> {
  const { error } = await supabase
    .from('event_signup_options')
    .delete()
    .eq('id', optionId);

  if (error) throw normalizeSupabaseError(error);
}

/** Admin: list allowlisted users for an invite-only event. */
export async function listEventInvitees(eventId: string): Promise<EventInvitee[]> {
  const { data, error } = await supabase.rpc('list_event_invitees', {
    p_event_id: eventId,
  });
  if (error) throw normalizeSupabaseError(error);
  return (data ?? []) as EventInvitee[];
}

/** Admin: add allowlist entry by existing auth user email. */
export async function addEventInviteeByEmail(
  eventId: string,
  email: string
): Promise<EventInvitee> {
  const { data, error } = await supabase.rpc('add_event_invitee_by_email', {
    p_event_id: eventId,
    p_email: email,
  });
  if (error) throw normalizeSupabaseError(error);
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('Invitee not returned');
  return row as EventInvitee;
}

/** Admin: remove allowlist entry. */
export async function removeEventInvitee(inviteeId: string): Promise<void> {
  const { error } = await supabase.from('event_invitees').delete().eq('id', inviteeId);
  if (error) throw normalizeSupabaseError(error);
}
